import type { SanityQueryOptions, SanityQueryRunner } from './query-runner';
import type { SanityPage } from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The image asset an image object points at, as its `_ref` (`image-<hash>-<w>x<h>-<ext>`). */
const imageAssetId = (value: Record<string, unknown>): string | undefined => {
  const ref = isObject(value.asset) ? value.asset._ref : undefined;
  return typeof ref === 'string' && ref.startsWith('image-') ? ref : undefined;
};

const collectAssetIds = (value: unknown, ids: Set<string>): void => {
  if (Array.isArray(value)) {
    for (const item of value) collectAssetIds(item, ids);
    return;
  }
  if (!isObject(value)) return;
  const id = imageAssetId(value);
  if (id) ids.add(id);
  for (const member of Object.values(value)) collectAssetIds(member, ids);
};

const markFrontify = (value: unknown, frontify: ReadonlySet<string>): unknown => {
  if (Array.isArray(value)) return value.map(item => markFrontify(item, frontify));
  if (!isObject(value)) return value;
  const marked = Object.fromEntries(
    Object.entries(value).map(([key, member]) => [key, markFrontify(member, frontify)]),
  );
  const id = imageAssetId(value);
  return id && frontify.has(id) ? { ...marked, assetSource: 'frontify' } : marked;
};

// Also a Sitecore media item stored in a `FRONTIFY` folder: Sitecore serves those from Frontify's CDN
// too, though the migration labels their source `sitecore`. Filtered on the server, so a draft
// read's stega-encoded fields are never compared here.
const FRONTIFY_ASSETS_QUERY = `*[_id in $ids && (source.name == "frontify" || "FRONTIFY" in string::split(originalFilename, "/"))]._id`;

/**
 * Marks every image on a page whose asset Sitecore serves from Frontify, which
 * `mappers.ts` needs to cut it as Sitecore does: Sitecore crops its own media items to a format,
 * but scales a Frontify image without a focal point to fit inside it.
 *
 * One query for the page's assets; a page without images asks nothing. Runs per request, on the page
 * with its shared content already filled in, so images there are marked too.
 */
export const createImageSourceResolver =
  (runner: SanityQueryRunner) =>
  async (page: SanityPage, options: SanityQueryOptions = {}): Promise<SanityPage> => {
    const ids = new Set<string>();
    collectAssetIds(page, ids);
    if (ids.size === 0) return page;
    const rows = await runner.query<unknown>(FRONTIFY_ASSETS_QUERY, { ids: [...ids] }, options);
    const frontify = new Set((Array.isArray(rows) ? rows : []).filter((id): id is string => typeof id === 'string'));
    return frontify.size > 0 ? (markFrontify(page, frontify) as SanityPage) : page;
  };
